import React from 'react';
import styled from '@emotion/styled';
import CodingConstants from 'constants/CodingConstants';
import { CODE_COLORS } from 'constants/CodeColorConstants';
import { useCodesById } from '../../../hooks/codes/queries';
import { uniq } from 'underscore';

const CodeSpan = styled.span`
  transition: box-shadow 0.3s ease-in-out;
  transition-delay: 0.3s;
  
  @media print {
    box-shadow: ${props => props.printBoxShadow} !important;
    -webkit-box-shadow: ${props => props.printBoxShadow} !important;
    print-color-adjust: exact;
    -webkit-print-color-adjust: exact;
  }
`;

const SubElementComponent = ({ subElement, subIndex, primeCode }) => {
  let style = {};
  const { codes } = useCodesById(subElement.codeIds);

  /**
   * Creates a multi-layered underline effect using box-shadows for multiple code colors
   * The underlines are stacked vertically as a series of overlapping borders
   * from bottom to top (last element to first in the array).
   * Each underline has a small gap between them for visual separation.
   * The first color in the array appears at the top, closest to the text,
   * while subsequent colors appear below in descending order.
   *
   * Example heights for colors ['red', 'blue', 'green']:
   * - 'red' (first color): 2px tall
   * - 'white' (gap): 3px tall
   * - 'blue' (second color): 5px tall (2px + 1px gap + 2px)
   * - 'white' (gap): 7px tall
   * - 'green' (third color): 8px tall (5px + 1px gap + 2px)
   *
   * @param {Array} colors - Array of color values to use for underlines
   * @returns {Object} CSS style object with box-shadow property and fade animation
   */
  const getUnderlineStyle = (colors = [], underlineThickness = 2, spread = 0) => {
    if (!colors.length) return {
      boxShadow: 'none',
    };

    const uniqueColors = uniq(colors).sort();
    const MAX_UNDERLINES = 3;
    const UNDERLINE_GAP = 1;
    let height = 0;

    // Take at most 3 colors and create stacked underlines
    const shadows = uniqueColors.slice(0, MAX_UNDERLINES).map((color, index) => {
      // Calculate position for each underline layer
      height += underlineThickness;
      if (index > 0) {
        height += UNDERLINE_GAP; // Add white gap between layers
      }
      // Create shadow pair: one for the colored line, one for the white gap
      return `0 ${height}px 0 ${spread}px ${color}, 0 ${height + UNDERLINE_GAP}px 0 0 white`;
    });

    return {
      boxShadow: shadows.join(', ')
    }
  };

  const PRINT_UNDERLINE_THICKNESS = 3;
  const PRINT_SPREAD = -1;

  let printBoxShadow = 'none';

  if (subElement.style === CodingConstants.CODED && !primeCode) {
    const codeColors = codes.map(code => CODE_COLORS[code?.color]);
    const screenStyle = getUnderlineStyle(codeColors);
    const printStyle = getUnderlineStyle(codeColors, PRINT_UNDERLINE_THICKNESS, PRINT_SPREAD);
    
    style = screenStyle;
    printBoxShadow = printStyle.boxShadow;
  } else if (subElement.style === CodingConstants.SUPERCODED) {
    const screenStyle = getUnderlineStyle([CODE_COLORS[primeCode?.color]]);
    const printStyle = getUnderlineStyle([CODE_COLORS[primeCode?.color]], PRINT_UNDERLINE_THICKNESS, PRINT_SPREAD);
    
    style = screenStyle;
    printBoxShadow = printStyle.boxShadow;
  }

  return (
    <CodeSpan
      className="codeSpan"
      data-code-index={subIndex}
      start={subElement.start}
      end={subElement.end}
      style={style}
      printBoxShadow={printBoxShadow}
    >
      {subElement.text}
    </CodeSpan>
  );
};

export default SubElementComponent;